import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { localizedMessageOf } from "@/lib/localized-error";
import { logApplicationFailure } from "@/lib/user-error";
import { usePendingEdits } from "./pending-edits";

// Hold an action until the reader has answered for their unsaved edits.
//
// UnsavedGuard already does this for NAVIGATION, where React Router hands it a blocker to reset or
// proceed. Nothing hands one over for an action that destroys edits without navigating — changing
// the acting-company context is exactly that: it empties the Apollo cache (ADR
// 0034-acting-company-context-resets-the-apollo-cache), which remounts every editor on screen and
// takes its local form state with it, while the URL never moves and no blocker ever fires.
//
// So the action is held here instead, against the same PendingEdits registry the SaveBar and
// UnsavedGuard read, and offered the same three answers. Nothing dirty anywhere means the action
// runs on the spot — a gate that asks when there is nothing to lose is a gate people learn to click
// through.

export interface UnsavedEditsGate {
  /**
   * Run `action`, or hold it and open the dialog when some scope has unsaved edits.
   *
   * The action must be idempotent-safe to defer: it runs later, from the dialog, in a render where
   * the caller's own props may have moved on.
   */
  request: (action: () => void) => void;
  /** True while an action is held — drive the dialog's `open` with it. */
  blocked: boolean;
  /** False when every dirty scope has nothing to save on the way out (`leaveOnly`): offer stay and leave only. */
  canSave: boolean;
  saving: boolean;
  /** Already localized. Empty unless the save from the dialog failed. */
  error: string;
  /** Drop the held action and keep the edits. */
  stay: () => void;
  /** Throw the edits away and run the held action. */
  discardAndProceed: () => void;
  /** Save every dirty scope first; run the held action only if all of them succeeded. */
  saveAndProceed: () => void;
}

export function useUnsavedEditsGate(): UnsavedEditsGate {
  const intl = useIntl();
  const context = usePendingEdits();
  // Wrapped in an object because a bare function in state would be taken for a state updater.
  const [held, setHeld] = useState<{ run: () => void } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const dirtyScopes = Object.values(context?.scopes ?? {}).filter((scope) => scope.dirty);

  function release(action: () => void): void {
    setHeld(null);
    setError("");
    action();
  }

  // A save already running when the action was held (the SaveBar's) can land meanwhile. Nothing is
  // left to lose then, and holding on would be the dialog asking about nothing — UnsavedGuard lets a
  // navigation through the same way.
  const nothingLeft = held !== null && dirtyScopes.length === 0 && !saving && !context?.saving;
  useEffect(() => {
    if (!nothingLeft || !held) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- releasing is the effect's whole job
    setHeld(null);
    setError("");
    held.run();
  }, [nothingLeft, held]);

  async function save(): Promise<void> {
    const action = held;
    if (!action || context?.saving) return;
    setSaving(true);
    setError("");
    try {
      await Promise.all(dirtyScopes.filter((scope) => !scope.leaveOnly).map((scope) => scope.save()));
      context?.signalSaved();
      release(action.run);
    } catch (e) {
      // The held action does NOT run: a failed save means the edits are still only in the browser,
      // and proceeding would destroy exactly what the save was meant to rescue.
      // @adr SPARK-0010-no-raw-server-errors-in-ui@1, and what makes the `error` field's contract above ("Already localized") true
      // rather than aspirational: only a LocalizedError's message reaches the screen. Every scope
      // rejects with one — that is the deliberate, already-translated refusal the reader needs —
      // and anything else is an accident that would have put genesis's own English prose in the
      // dialog. Re-running the message through userErrorMessage would be worse than either: the
      // text is already product copy, so it would be replaced by a generic "something went wrong".
      const localized = localizedMessageOf(e);
      if (!localized) logApplicationFailure("unsaved-edits-gate save");
      setError(
        localized ?? intl.formatMessage({ id: "common.saveFailed", defaultMessage: "Save failed." }),
      );
    } finally {
      setSaving(false);
    }
  }

  return {
    request: (action) => {
      // A save still running is not done with the page either: the profile's save reloads after its mutations,
      // and resetting the store under that reload failed the save. The effect above runs the action then.
      if (dirtyScopes.length === 0 && !context?.saving) {
        action();
        return;
      }
      setError("");
      setHeld({ run: action });
    },
    blocked: held !== null,
    canSave: dirtyScopes.some((scope) => !scope.leaveOnly),
    saving: saving || Boolean(context?.saving),
    error,
    stay: () => {
      setHeld(null);
      setError("");
    },
    discardAndProceed: () => {
      const action = held;
      if (!action) return;
      dirtyScopes.forEach((scope) => scope.discard());
      release(action.run);
    },
    saveAndProceed: () => {
      void save();
    },
  };
}
